<template>
  <div :class="b(componentModifiers)">
    <div :class="b('message')">
      {{ notification.message }}
    </div>
    <button
      :class="b('close-button')"
      type="button"
      @click="close"
    >
      <e-icon
        icon="i-close"
        size="20"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import useNotificationStore, { MappedNotificationItem } from '@/stores/notification';
  import eIcon from '@/elements/e-icon.vue';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  type Setup = {
    notificationStore: ReturnType<typeof useNotificationStore>;
  };

  type Data = {
    expireDelay: number;
  };

  /**
   * Notification component to be used within c-notification-container. See /styleguide/notifications for demo.
   */
  export default defineComponent({
    name: 'c-notification',

    components: {
      eIcon,
    },

    props: {
      /**
       * The notification object consisting of the following properties:
       */
      notification: {
        type: Object as PropType<MappedNotificationItem>,
        required: true,
      },
    },

    setup(): Setup {
      return {
        notificationStore: useNotificationStore(),
      };
    },
    data(): Data {
      return {
        /**
         * Defines the delay a notification expires in Milliseconds.
         */
        expireDelay: 3000,
      };
    },

    computed: {
      /**
       * Returns all modifiers for the component main class.
       */
      componentModifiers(): Modifiers {
        return {
          type: this.notification.type,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const { expire, secondsToShowInMs } = this.notification || {};

      const delay = secondsToShowInMs || this.expireDelay;

      if (expire) {
        setTimeout(() => {
          this.close();
        }, delay);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Removes current notification from stack.
       */
      close() {
        this.notificationStore.popNotification(this.notification.id);
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-notification {
    position: relative;
    min-width: 280px;
    max-width: 280px;
    min-height: 60px;
    display: flex;
    align-items: center;
    padding: variables.$va-spacing--15;
    font-weight: 600;

    @include mixins.media(xs) {
      min-width: 400px;
      max-width: 400px;
    }

    &--type-success {
      background-color: variables.$va-color-status--success;
      color: variables.$va-color-grayscale--1000;
    }

    &--type-info {
      background-color: variables.$va-color-status--info;
      color: variables.$va-color-grayscale--0;
    }

    &--type-error {
      background-color: variables.$va-color-status--error;
      color: variables.$va-color-grayscale--1000;
    }

    &__message {
      width: 100%;
      padding-right: variables.$va-spacing--30;
    }

    &__close-button {
      position: absolute;
      top: variables.$va-spacing--5;
      right: variables.$va-spacing--5;
      color: inherit;
    }
  }
</style>
